import {
  NormalizedDragEvent,
  useDraggable,
  useDroppable,
} from '@progress/kendo-react-common';
import * as React from 'react';
import {
  RefObject,
  useCallback,
  useContext,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import { EmployeeTemplateOptionModel } from '../../../../../models/EmployeeTemplateOptionModel';
import { DirectionContext, DragContext, DropContext } from './DragContext';
import { ListItem, ListItemProps } from './ListItem';
import { OptionPanel } from './OptionPanel';

export interface DraggableListItemProps extends ListItemProps {
  onDrop: Function;
}

export const DraggableListItem = (props: DraggableListItemProps) => {
  const item = useRef<HTMLLIElement>(null);
  const hint = useRef<HTMLLIElement>(null);
  const dropHint = useRef<HTMLLIElement>(null);
  const dragInstance = useRef<{
    element: RefObject<HTMLElement | null>;
    data: EmployeeTemplateOptionModel;
  }>(null);
  const dropInstance = React.useRef<{
    element: RefObject<HTMLElement | null>;
    data: EmployeeTemplateOptionModel;
  }>(null);

  useImperativeHandle(dragInstance, () => ({
    element: hint,
    data: props.data,
  }));
  useImperativeHandle(dropInstance, () => ({
    element: item,
    data: props.data,
  }));

  const [drag, setDrag] = useContext(DragContext);
  const [drop, setDrop] = useContext(DropContext);
  const [direction, setDirection] = useContext(DirectionContext);

  const [dragged, setDragged] = useState<boolean>(false);
  const [offset, setOffset] = useState<{ x: number; y: number }>({
    x: 0,
    y: 0,
  });
  const [position, setPosition] = useState<{
    x: number;
    y: number;
  } | null>(null);
  const [width, setWidth] = useState<number | null>(null);

  const handlePress = useCallback((event: NormalizedDragEvent) => {
    setOffset({
      x: event.offsetX - window.scrollX,
      y: event.offsetY - window.scrollY,
    });

    if (!item.current) {
      return;
    }
    setWidth(item.current.offsetWidth);
  }, []);

  const handleDragStart = React.useCallback(() => {
    setDragged(true);
    setDrag(dragInstance.current);
  }, [setDrag]);

  const handleDrag = React.useCallback(
    (event: NormalizedDragEvent) => {
      if (drag?.element.current && drop?.element.current) {
        const newDirection = calculateDirection(
          drag.element.current,
          drop.element.current,
        );
        setDirection(newDirection);
      }
      setPosition({
        x: event.clientX - offset.x,
        y: event.clientY - offset.y,
      });
    },
    [drag, drop, offset.x, offset.y, setDirection],
  );

  const handleDragEnd = React.useCallback(() => {
    setDragged(false);
    setOffset({ x: 0, y: 0 });
    setDrag(null);
  }, [setDrag]);

  const handleRelease = React.useCallback(() => {
    setDrop(null);
  }, [setDrop]);

  const handleDragEnter = React.useCallback(() => {
    setDrop(dropInstance.current);
  }, [setDrop]);

  const handleDragLeave = React.useCallback(() => {
    setDrop(null);
  }, [setDrop]);

  const handleDrop = React.useCallback(() => {
    if (props.onDrop) {
      props.onDrop();
    }
    setDrop(null);
  }, [props, setDrop]);

  useDraggable(
    item,
    {
      onPress: handlePress,
      onDragStart: handleDragStart,
      onDrag: handleDrag,
      onDragEnd: handleDragEnd,
      onRelease: handleRelease,
    },
    {
      hint,
      autoScroll: false,
    },
  );

  useDroppable(item, {
    onDragEnter: handleDragEnter,
    onDragLeave: handleDragLeave,
    onDrop: handleDrop,
  });
  useDroppable(dropHint, {
    onDragEnter: handleDragEnter,
    onDrop: handleDrop,
  });

  return (
    <React.Fragment>
      {drag &&
        drop &&
        drop.data.optionId === props.data.optionId &&
        direction === 'before' && (
          <ListItem ref={dropHint} style={{ opacity: '0.7' }}>
            <OptionPanel
              key={drag.data.optionId}
              option={drag.data}
              saveOptionNewName={drag.saveOptionNewName}
              removeOption={drag.removeOption}
            />
          </ListItem>
        )}
      <ListItem
        ref={item}
        {...props}
        data={{ type: '' }}
        style={
          dragged ? { ...props.style, display: 'none' } : { ...props.style }
        }
      />
      {drag &&
        drop &&
        drop.data.optionId === props.data.optionId &&
        direction === 'after' && (
          <ListItem ref={dropHint} style={{ opacity: '0.7' }}>
            <OptionPanel
              key={drag.data.optionId}
              option={drag.data}
              saveOptionNewName={drag.saveOptionNewName}
              removeOption={drag.removeOption}
            />
          </ListItem>
        )}
      {dragged && position && (
        <ListItem
          ref={hint}
          {...props}
          style={
            {
              position: 'absolute',
              width,
              left: position.x,
              top: position.y,
            } as React.CSSProperties
          }
        />
      )}
    </React.Fragment>
  );
};

const calculateDirection = (drag: HTMLElement, drop: HTMLElement) => {
  const dragRect = drag.getBoundingClientRect();
  const dropRect = drop.getBoundingClientRect();

  return dragRect.top + dragRect.height / 2 < dropRect.top + dropRect.height / 2
    ? 'before'
    : 'after';
};
